import { computed } from 'mobx';
import { getItemConfig, getMonthItemConfig, monthDayCellConfig } from './OwaCalendarItemConfig';
import { onSurfaceClick, onSurfaceDoubleClick } from '../actions/surfaceOnClickActions';
import { getCalendarFolderId } from 'owa-calendar-cache';
import { getCalendarColorScheme, getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { unselectCalendar } from 'owa-calendar-surface-select-calendar';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { getSelectedCalendars } from 'owa-calendar-surface-calendars-store';
import { isPreviewItemId } from 'owa-quick-compose-controller';
import { CalendarEvent, compareCalendarEvents } from 'owa-calendar-types';
import { getScenarioStore, SearchScenarioId } from 'owa-search';
import { dismissQuickComposeIfNotDirty } from 'owa-calendar-compose-form-actions';
import { lazyIsQuickComposeOpen } from 'owa-calendar-compose-form-store';
import { WeatherSurfaceItem } from 'owa-weather';
import { getAllDayWellHeight, getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import {
    shouldShowWeekMonthName,
    shouldShowWeekDayTemp,
    shouldShowWeekWeatherIcon,
    shouldShowDynamicWeekColumns,
    shouldShowFullWeekDayName,
} from 'owa-calendar-layout';
import * as React from 'react';
import {
    CalendarSurfaceTransformProps,
    LinearItemsLayout,
    DatesColumnProvider,
    getDynamicColumnWidthRatios,
    getDefaultColumnWidthRatios,
    freeBusyTemplate,
    contentHeaderTemplate,
} from 'owa-calendar-surface-dategrid';
import {
    TypedCalendarDayContentConfig,
    CalendarSurfaceConfig,
    CalendarMonthConfig,
    FocusableSurface,
} from 'owa-calendar-surface-core';
import {
    CalendarMonthTransformProps,
    createCalendarDayContent,
    getCalendarMonthContent,
    updateFocusedSurfaceElement,
    getSurfaceItems,
} from 'owa-calendar-surface';
import {
    canDragItem,
    canDropItemType,
    onDragEnd,
    onDragStart,
    onItemDropped,
    getSurfaceItemForPreview,
    setCanDropCalendarItem,
} from 'owa-calendar-surface-view-store';
import { isOpxAndTeams } from 'owa-opx';
import { containsText } from 'owa-calendar-event-capabilities';
import InDayComponent, { InDayAdditionalPropsType } from '../components/InDayComponent';
import AllDayComponent, { AllDayAdditionalPropsType } from '../components/AllDayComponent';
import AllDayHeaderComponent, {
    AllDayHeaderAdditionalPropsType,
} from '../components/AllDayHeaderComponent';
import MonthSurfaceComponent, {
    MonthSurfaceAdditionalPropsType,
} from '../components/MonthSurfaceComponent';
import InDayContentComponent, {
    InDayContentAdditionalPropsType,
} from '../components/InDayContentComponent';
import { getCalendarFolderWorkingHours } from 'owa-calendar-events-store';
import { CalendarLayoutSplitViewConstants } from 'owa-calendar-layout';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { isBrowserIE } from 'owa-user-agent';

const styles = require('./OwaCalendarSurfaceConfig.scss');

/**
 * Properties for generating day/week content
 */
export let surfaceDayContentConfig: CalendarSurfaceTransformProps = {
    onSurfaceClick: onSurfaceClick,
    onSurfaceDoubleClick: onSurfaceDoubleClick,
    columnProvider: new DatesColumnProvider(surfaceStore, models =>
        // This check MUST be in a callback; feature flags are not yet loaded when we create the config.
        shouldShowDynamicWeekColumns()
            ? getDynamicColumnWidthRatios(models)
            : getDefaultColumnWidthRatios(models)
    ),
    itemsLayout: new LinearItemsLayout(surfaceStore),
    renderDateHeaderAddOn: date =>
        !isOpxAndTeams() && shouldShowWeekWeatherIcon() ? (
            <WeatherSurfaceItem
                date={date}
                showInTopRight={true}
                shouldShowWeekDayTemp={shouldShowWeekDayTemp}
            />
        ) : undefined,
    dragDropConfig: {
        canDrag: canDragItem,
        canDrop: canDropItem,
        onItemDropped: onItemDropped,
        onDragStart: onDragStart,
        onDragEnd: onDragEndResetCanDrop,
        getSurfaceItemForPreview: getSurfaceItemForPreview,
        colorProvider: getCalendarEventColorScheme,
    },
    getItemConfig: getItemConfig,
    freeBusyTemplate: (model: CalendarEvent) =>
        freeBusyTemplate(
            model.ItemId.Id,
            styles.freeBusyColumnShadow,
            getCalendarEventColorScheme(model)
        ),
    supportExpandableAllDayWell: () => true,
    shouldShowCurrentTime: () => true,
    shouldShowWeekMonthName: shouldShowWeekMonthName,
    timeStripConfig: {
        highlightCurrentTime: true,
    },
};

/**
 * Properties for generating month content
 */
let surfaceMonthContentConfig: CalendarMonthTransformProps = {
    monthDayCellsConfig: monthDayCellConfig,
    renderDateHeaderAddOn: date =>
        !isOpxAndTeams() && shouldShowWeekWeatherIcon() ? (
            <WeatherSurfaceItem
                date={date}
                showInTopRight={false}
                shouldShowWeekDayTemp={shouldShowWeekDayTemp}
            />
        ) : undefined,
    dragDropConfig: {
        canDrag: canDragItem,
        canDrop: canDropItemType,
        onItemDropped: onItemDropped,
        onDragStart: onDragStart,
        onDragEnd: onDragEnd,
        getSurfaceItemForPreview: getSurfaceItemForPreview,
        colorProvider: getCalendarEventColorScheme,
    },
    getItemConfig: getMonthItemConfig,
    shouldShowWeekMonthName: shouldShowWeekMonthName,
    shouldShowFullWeekDayName: shouldShowFullWeekDayName,
};

/**
 * Properties for Calendar Surface core controls: Day/Week and Month
 */

let surfaceConfig: CalendarSurfaceConfig = {
    dayViewConfig: {
        supportSplitView: () => {
            return surfaceStore.isInSplitView;
        },
        // TODO: WI 97881 Remove minWidthProvider() in 1 and 2 day view split view scenarios
        minWidthProvider: () =>
            surfaceStore.isInSplitView &&
            surfaceStore.dateRangeType == DateRangeType.Day &&
            getNumDaysInDayRange() <= 2
                ? CalendarLayoutSplitViewConstants.selectedCalendarMinWidth_DAY
                : surfaceStore.isInSplitView && isBrowserIE()
                ? CalendarLayoutSplitViewConstants.selectedCalendarMinWidth_WEEK
                : undefined,
        supportExpandableAllDayWell: surfaceDayContentConfig.supportExpandableAllDayWell,
        allDayWellCollapsedHeightProvider: () => getAllDayWellHeight(),
        setFocusedElement: setFocusedElement,
        calendarDayContentConfigProvider: () => getCalendarDayContentConfigs.get(),
        onSurfaceScroll: () =>
            lazyIsQuickComposeOpen
                .import()
                .then(
                    isQuickComposeOpen => isQuickComposeOpen() && dismissQuickComposeIfNotDirty()
                ),
    },
    monthViewConfig: getCalendarMonthViewConfig(),
};

function getCalendarMonthViewConfig(): CalendarMonthConfig<MonthSurfaceAdditionalPropsType> {
    return {
        contentGenerator: getMonthContent,
        setFocusedElement: setFocusedElement,
        monthSurfaceComponent: {
            component: MonthSurfaceComponent,
            additionalProps: {
                surfaceStore: surfaceStore,
                calendarConfig: surfaceDayContentConfig,
            },
        },
    };
}

/**
 * Computed to avoid functional reference changing unless store values change
 */
const getCalendarDayContentConfigs = computed(
    function getCalendarDayContentConfigs(): TypedCalendarDayContentConfig<
        AllDayAdditionalPropsType,
        AllDayHeaderAdditionalPropsType,
        InDayAdditionalPropsType,
        InDayContentAdditionalPropsType
    >[] {
        return surfaceStore.isInSplitView
            ? getSplitViewCalendarDayContentConfigs()
            : getMergedViewCalendarDayContentConfigs();
    }
);

function getSplitViewCalendarDayContentConfigs() {
    let searchStore = getScenarioStore(SearchScenarioId.Calendar);
    let searchText = searchStore.searchText;

    return getSelectedCalendars().map((calendar, index) => {
        let calendarFolderId = getCalendarFolderId(calendar);
        let calendarWorkingHours = getCalendarFolderWorkingHours(calendarFolderId);
        let colorScheme =
            calendar.colorScheme == null
                ? getCalendarColorScheme(calendar.id)
                : calendar.colorScheme;

        const calendarDayContent = createCalendarDayContent(
            surfaceStore,
            surfaceDayContentConfig,
            () => {
                let itemsPerCalendar = filterClientItems(searchText);
                return itemsPerCalendar.filter(item => item.ParentFolderId.Id == calendarFolderId);
            },
            calendarFolderId,
            customEventsComparator,
            undefined,
            undefined,
            calendarWorkingHours
        );

        return {
            content: calendarDayContent,
            contentDescription: () =>
                contentHeaderTemplate(index.toString(), calendar.displayName, colorScheme, () =>
                    unselectCalendar(calendar)
                ),
            colorScheme: colorScheme,
            allDayComponent: {
                component: AllDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                    calendarDayContent: calendarDayContent,
                },
            },
            allDayHeaderComponent: {
                component: AllDayHeaderComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
            inDayComponent: {
                component: InDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                },
            },
            inDayContentComponent: {
                component: InDayContentComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
        };
    });
}

function getMergedViewCalendarDayContentConfigs() {
    let searchStore = getScenarioStore(SearchScenarioId.Calendar);
    let searchText = searchStore.searchText;
    const calendarDayContent = createCalendarDayContent(
        surfaceStore,
        surfaceDayContentConfig,
        () => {
            return filterClientItems(searchText);
        },
        undefined,
        customEventsComparator
    );

    return [
        {
            content: calendarDayContent,
            allDayComponent: {
                component: AllDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarFolderId: undefined,
                    calendarConfig: surfaceDayContentConfig,
                    calendarDayContent: calendarDayContent,
                },
            },
            allDayHeaderComponent: {
                component: AllDayHeaderComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarFolderId: undefined,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
            inDayComponent: {
                component: InDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                },
            },
            inDayContentComponent: {
                component: InDayContentComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
        },
    ];
}

function getMonthContent() {
    return getCalendarMonthContent(
        surfaceStore,
        surfaceMonthContentConfig,
        getScenarioStore(SearchScenarioId.Calendar).searchText
    );
}

function setFocusedElement(id: string) {
    updateFocusedSurfaceElement(id, surfaceStore);
}

function isClientSideFilteringEnabled(searchText: string) {
    return searchText && searchText.length > 0;
}

function filterClientItems(searchText: string) {
    let items = getSurfaceItems(surfaceStore);
    if (isClientSideFilteringEnabled(searchText)) {
        return items.filter(
            item => isPreviewItemId(item.ItemId.Id) || containsText(item, searchText)
        );
    } else {
        return items;
    }
}

function customEventsComparator(item1: CalendarEvent, item2: CalendarEvent): number {
    // For all day events, move the event to the front if it is highlighted in the grid
    if (isPreviewItemId(item1.ItemId.Id)) {
        return -1;
    } else if (isPreviewItemId(item2.ItemId.Id)) {
        return 1;
    }

    // Apply default sorting for non-all day or non-highlighted events
    return compareCalendarEvents(item1, item2);
}

/**
 * Used to account for the dropping of items onto other calendars or tasks from the time panel to surface.
 * Sets the global canDrop to determine drag icon.
 * @param dragItemType
 * @param droppableCalendarFolderId
 */
function canDropItem(dragItemType: string, droppableCalendarFolderId: string): boolean {
    if (canDropItemType(dragItemType, droppableCalendarFolderId)) {
        setCanDropCalendarItem(true);
        return true;
    }
    setCanDropCalendarItem(false);
    return false;
}

/**
 * Resets canDrop of CalendarItems for the global calendar module droppable when dragging stops.
 * @param surfaceItem
 */
function onDragEndResetCanDrop(surfaceItem: CalendarEvent) {
    setCanDropCalendarItem(false);
    onDragEnd(surfaceItem);
}

let surfaceComponent: FocusableSurface;
surfaceConfig.surfaceComponent = surface => {
    surfaceComponent = surface;
};

surfaceConfig.dayViewConfig.surfaceComponent = surfaceConfig.surfaceComponent;
surfaceConfig.monthViewConfig.surfaceComponent = surfaceConfig.surfaceComponent;

export function getFocusableSurfaceComponent() {
    return surfaceComponent;
}

export default surfaceConfig;
